/**
 * The onboarding screens' AUTHORED Bangla — every sentence A1–A7 and the recovery form
 * show that `Oprappo P0 Screens.dc.html` does not draw and the Worker does not send.
 *
 * `copy_status: 'authored'`: written by W2-12 under the founder's delegation of undrawn
 * UI copy (2026-09-18), and read by no native speaker yet. Each string is listed, with
 * where it renders and what a wrong version does to the reader, in
 * `docs/copy-review/W2-12.md`; change one here and nowhere else. The DOM marks every
 * element that carries one with `data-copy-status="authored"`, so a running page says
 * which of its sentences are unreviewed.
 *
 * Three rules each string was written against, from `P0 States § চারটি নিয়ম` and CLAUDE.md:
 * say what happened and the next step, not a code; one language per sentence (so
 * Telegram's START is written স্টার্ট); and none of the five banned words —
 * `__tests__/onboarding.test.tsx` walks this object for them.
 *
 * A string marked SOURCED is drawn somewhere in the design set and reused, so the person
 * meets words they have already seen. It is listed beside the authored ones so a
 * reviewer reads each screen whole.
 */
import type { CopyStatus } from '@oprappo/contracts';

export const ONBOARDING_COPY = {
  copy_status: 'authored' satisfies CopyStatus,

  /* ───────────────────────────────────────── A3 / A7 · the door ── */
  door: {
    /** Above the two channel rows. The rows themselves are the Worker's (`/v1/auth/channels`). */
    channel_label_bn: 'কোড কোথায় পাঠাব?',
    /** SOURCED: `Oprappo Design System.dc.html § 02 Input`, the error line's own example. */
    number_invalid_bn: '১১ সংখ্যার নম্বর দিন',
    /** The button, while Telegram is chosen and not yet opened for this number. */
    open_telegram_bn: 'টেলিগ্রাম খুলুন',
    /** A request nothing more can be said about. Names what did not happen. */
    not_sent_bn: 'কোড পাঠানো যায়নি। একটু পরে আবার চেষ্টা করুন।',
    /** `POST /telegram/start-link` answered 503: no bot is configured. */
    telegram_unavailable_bn: 'টেলিগ্রামে এখন কোড পাঠানো যাচ্ছে না। হোয়াটসঅ্যাপে কোড নিন।',
  },

  /* ─────────────────────────── the Telegram step (A3, A4, A7) ── */
  telegram: {
    step_title_bn: 'টেলিগ্রামে স্টার্ট চাপুন',
    step_body_bn: 'টেলিগ্রামে অপ্রাপ্য-র বট খুলবে। সেখানে স্টার্ট চাপুন, তারপর এখানে ফিরে ‘কোড পাঠান’ চাপুন।',
    /** A link, for when the new tab was blocked or closed. */
    reopen_bn: 'টেলিগ্রাম আবার খুলুন',
  },

  /* ───────────────────────────────────────────── A4 · the code ── */
  code: {
    /** SOURCED: A4's drawn "আবার পাঠানো যাবে ০:৪২ পরে", with the live time in the drawn place. */
    resend_in_bn: 'আবার পাঠানো যাবে {clock} পরে',
    resend_bn: 'আবার পাঠান',
    resent_bn: 'নতুন কোড পাঠানো হয়েছে।',
    /** A verification nothing more can be said about. */
    not_checked_bn: 'কোডটি যাচাই করা যায়নি। একটু পরে আবার চেষ্টা করুন।',
    /** Replaces the drawn "এসএমএস আসছে না?" — no code travels by SMS. */
    help_title_bn: 'কোড আসছে না?',
    /** Replaces the drawn "কল করে কোড শুনতে পারেন।" — there is no voice channel. */
    help_body_bn: 'টেলিগ্রামে কোড নিতে পারেন।',
    /** Replaces the drawn "কল করে শোনান". Worded by the W2-12 brief. */
    telegram_bn: 'টেলিগ্রামে কোড নিন',
  },

  /* ─────────────────────────────── A7's two links · recovery ── */
  recovery: {
    /** SOURCED: A7's own link text, as the heading of the page it opens. */
    number_title_bn: 'নম্বর বদলে গেছে?',
    /** SOURCED: A7's own link text. */
    help_title_bn: 'সহায়তা নিন',
    number_intro_bn:
      'পুরোনো নম্বর আর এখন যে নম্বরে আপনাকে পাওয়া যাবে, দুটোই লিখুন। একজন মানুষ পরিচয় যাচাই করে যোগাযোগ করবেন।',
    help_intro_bn: 'কী সমস্যা হচ্ছে লিখুন। একজন মানুষ আপনার দেওয়া নম্বরে যোগাযোগ করবেন।',
    old_number_label_bn: 'পুরোনো নম্বর',
    contact_label_bn: 'যে নম্বরে পাওয়া যাবে',
    details_label_bn: 'কী হয়েছে',
    details_hint_bn: 'সংক্ষেপে লিখুন।',
    details_required_bn: 'কী হয়েছে, একটু লিখুন',
    /** SOURCED: § 01's accessible name for send (`@oprappo/tokens § ariaLabel.send`). */
    submit_bn: 'অনুরোধ পাঠান',
    sent_title_bn: 'অনুরোধ পাঠানো হয়েছে',
    sent_body_bn: 'একজন মানুষ আপনার দেওয়া নম্বরে যোগাযোগ করবেন।',
    /** The Worker serves no route for this yet. Said as it is, not as a failure of theirs. */
    closed_bn: 'এখান থেকে অনুরোধ নেওয়া এখনও চালু হয়নি।',
    not_sent_bn: 'অনুরোধ পাঠানো যায়নি। একটু পরে আবার চেষ্টা করুন।',
    done_bn: 'লগইনে ফিরে যান',
  },
} as const;

/** A4's drawn sentence with the live time slotted in. `{clock}` is the only substitution. */
export function resendInBn(clockBn: string): string {
  return ONBOARDING_COPY.code.resend_in_bn.replace('{clock}', clockBn);
}
